import { type FormEvent, useState } from "react";

import { useServerData } from "@hooks/useServerData";

import { getCSRFToken } from "../../rest_client";
import { Input, Textarea } from "../base";

import * as styles from "./Contact.styles";

const Contact = () => {
  const { recaptchaSiteKey } = useServerData();
  const [sending, setSending] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    from: "",
    subject: "",
    name: "",
    text: "",
  });

  const generateErrorMessages = () => {
    const newErrorMessages: Record<string, string> = {};

    if (formData.from === "") newErrorMessages.from = "Email field is required";
    if (formData.subject === "") newErrorMessages.subject = "Subject field is required";
    if (formData.text === "") newErrorMessages.text = "Message field is required";
    if (formData.name === "") newErrorMessages.name = "Name field is required";
    return newErrorMessages;
  };

  const handleChange = (name: string | undefined, value: string) => {
    setSuccess(false);
    setError(false);

    if (name) {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    setSending(true);

    const newErrorMessages = generateErrorMessages();
    if (Object.keys(newErrorMessages).length > 0) {
      setErrorMessages(newErrorMessages);
      setSending(false);
      return;
    }

    fetch("/contact_email", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": getCSRFToken(),
      },
      cache: "no-cache",
      credentials: "same-origin",
      body: data,
    })
      .then(response => {
        return response.json();
      })
      .catch(() => {
        setSending(false);
        setErrorMessages({});
        setError(true);
        grecaptcha.reset();
      })
      .then(() => {
        setSending(false);
        setSuccess(true);
        setErrorMessages({});
        setError(false);
        setFormData({
          from: "",
          subject: "",
          name: "",
          text: "",
        });
        grecaptcha.reset();
      });
  };

  const getAjaxStatusMessageNode = () => {
    if (success) {
      return <div className="success">Email successfully sent!</div>;
    } else if (error) {
      return <div className="error">An error occurred. Please try again.</div>;
    }
  };

  return (
    <div id="contact-component" css={styles.contact}>
      <div className="row">
        <div className="small-4 columns">
          <div className="divider" />
        </div>
        <div className="small-4 columns">
          <h2 className="text-center">Get in Touch</h2>
        </div>
        <div className="small-4 columns">
          <div className="divider" />
        </div>
      </div>
      <div className="row container">
        <div className="small-12 medium-5 columns">
          <div className="row" id="location">
            <ul css={styles.locationList}>
              <li>
                <address css={styles.address}>
                  <i css={styles.mapMarker} className="fa fa-map-marker" aria-hidden="true" />
                  407 B Pitt Street Cornwall, ON KCJ 3R3
                </address>
              </li>
              <li>
                <i className="fa fa-phone red" aria-hidden="true" /> (613) 935-5777
              </li>
              <li>
                <i className="fa fa-fax red" aria-hidden="true" /> (613) 935-3311
              </li>
            </ul>
          </div>
          <div className="row">
            <a href="https://www.google.ca/maps/place/ExSELLence+Team+Realty+Inc./@45.0214745,-74.7324119,17z/data=!3m1!4b1!4m5!3m4!1s0x4ccc19e27de215e5:0xee1cb0b12b525385!8m2!3d45.0276717!4d-74.7342983">
              <img
                css={styles.img}
                src="/map.jpg"
                alt="Exsellence Team Realty Inc. Brokerage location"
              />
            </a>
          </div>
        </div>
        <div className="small-12 medium-6 columns">
          <div css={styles.form}>
            <h3 css={styles.header}>Leave us a message about anything</h3>

            <form onSubmit={handleSubmit}>
              <Input
                placeholder="Name"
                id="name"
                name="name"
                aria-label="Your name"
                value={formData.name}
                onChange={handleChange}
                errorMessages={errorMessages}
              />

              <Input
                placeholder="Email"
                type="email"
                name="from"
                id="email"
                aria-label="Your contact email"
                value={formData.from}
                onChange={handleChange}
                errorMessages={errorMessages}
                error="from"
              />

              <Input
                placeholder="Subject"
                name="subject"
                id="subject"
                aria-label="Subject you're contacting about"
                value={formData.subject}
                onChange={handleChange}
                errorMessages={errorMessages}
              />

              <Textarea
                placeholder="Message"
                id="text"
                name="text"
                aria-label="message"
                value={formData.text}
                onChange={handleChange}
                errorMessages={errorMessages}
                error="text"
              />

              <div className="field form-control">
                <div className="g-recaptcha" data-sitekey={recaptchaSiteKey} />
              </div>

              <div className="field form-control">
                <button className="btn btn-large" type="submit" name="button" disabled={sending}>
                  Send
                </button>
              </div>

              {getAjaxStatusMessageNode()}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Contact };
